import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TableWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import { applyContentItemOverrides } from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	GetSocialListeningMyBrandAnalyticsTopCountriesService,
	TopCountryItem
} from '../../../../../../services/get-social-listening-my-brand-analytics-top-countries.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

// Categorical bar palette — one color per country row, in order, mirroring
// the LOLLY report mockup (Figma 13302:101592). Cycles if there are >10 rows.
const BAR_COLORS = [
	'#2872e1',
	'#e94f6a',
	'#2bb673',
	'#a98cf0',
	'#f5c451',
	'#6cd6c8',
	'#f0a3c6',
	'#5b8def',
	'#9aa7c7',
	'#cbb892'
];

interface CountryBar {
	name: string;
	mentions: number;
	color: string;
	widthPct: number;
}

@Component({
	standalone: true,
	selector: 'app-top-countries-slide',
	templateUrl: './top-countries-slide.component.html',
	styleUrls: ['./top-countries-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		DecimalPipe,
		TypographyComponent,
		IconComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class TopCountriesSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();
	private destroy$ = new Subject<void>();
	private cachedCountries: TopCountryItem[] | null = null;
	private cacheKey: string | null = null;

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	tableAccentColor = '#2872e1';
	bars: CountryBar[] = [];
	topSharePct = '';
	topShareLabel = '';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'top_countries',
		null,
		null
	);

	get isEmpty(): boolean {
		return !this.bars.length;
	}

	constructor(
		private analyticsService: GetSocialListeningMyBrandAnalyticsTopCountriesService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedCountries && this.cacheKey === currentCacheKey) {
			this.applyCountries(this.cachedCountries);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(countries => {
				this.cachedCountries = countries;
				this.cacheKey = currentCacheKey;
				this.applyCountries(countries);
			});
	}

	private applyCountries(countries: TopCountryItem[]): void {
		const rawSlide = this.buildSlide(countries);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(countries: TopCountryItem[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'top_countries',
			title: this.item.title || 'Top Countries',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Top Countries',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				{
					id: `${prefix}_table`,
					kind: 'table',
					visible: true,
					content: {
						dataSourceKey: 'top_countries_table',
						accentColor: '#2872e1',
						columns: [
							{ key: 'name', label: 'Country' },
							{ key: 'mentions', label: 'Mentions' }
						],
						rows: countries.map(c => ({
							name: c.name,
							mentions: String(c.mentions)
						}))
					}
				}
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		this.dateLabel =
			this.getTitleWidget('date_range', hidden, slide)?.content.text ??
			slide.dateRange?.label ??
			'';

		this.summaryText =
			this.getSummaryWidget(hidden, slide)?.content.text ??
			slide.description ??
			'';
		this.title =
			this.getTitleWidget('title', hidden, slide)?.content.text ??
			slide.title;
		this.titleColor =
			this.getTitleWidget('title', hidden, slide)?.content.color ??
			'#2872e1';

		const table = slide.widgets.find(
			w => w.kind === 'table' && !hidden.has(w.id)
		) as TableWidget | undefined;
		this.tableAccentColor = table?.content.accentColor ?? '#2872e1';

		const rows = (table?.content.rows ?? []).map(r => ({
			name: r['name'] ?? '',
			mentions: parseInt(r['mentions'] ?? '0', 10)
		}));

		this.buildBars(rows);
	}

	private buildBars(rows: { name: string; mentions: number }[]): void {
		const maxVal = rows.reduce((m, r) => Math.max(m, r.mentions), 0);
		const total = rows.reduce((s, r) => s + r.mentions, 0);

		this.bars = rows.slice(0, 10).map((r, i) => ({
			name: r.name,
			mentions: r.mentions,
			color: BAR_COLORS[i % BAR_COLORS.length],
			// TRAP: 85, not 100 — the value label follows the longest bar and needs
			// room inside the slide (top-sites-by-visits-slide does the same).
			widthPct: maxVal > 0 ? (r.mentions / maxVal) * 85 : 0
		}));

		// The same ten countries, each in the colour its bar has here.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: this.bars.map(bar => bar.name),
				series: [
					{
						key: 'mentions',
						label: 'Mentions',
						values: this.bars.map(bar => bar.mentions)
					}
				],
				labelColors: this.bars.map(bar => bar.color),
				labelColumn: 'Country'
			})
		);

		// KPI callout — leading country's share of the mentions we could place
		// on a map. Country is only persisted above a confidence threshold, so
		// this base is a subset of total mention volume (~a third of it in
		// practice); the label names the base, otherwise the number reads as a
		// share of every mention and overstates the leader roughly threefold.
		const top = rows.reduce(
			(best, r) => (r.mentions > best.mentions ? r : best),
			{ name: '', mentions: 0 }
		);
		if (total > 0 && top.mentions > 0) {
			this.topSharePct = `${((top.mentions / total) * 100).toFixed(1)}%`;
			this.topShareLabel = `${top.name} of located mentions`;
		} else {
			this.topSharePct = '';
			this.topShareLabel = '';
		}
	}

	private getTitleWidget(
		kind: TitleWidget['kind'],
		hidden: Set<string>,
		slide: PreviewSlide
	): TitleWidget | undefined {
		return slide.widgets.find(w => w.kind === kind && !hidden.has(w.id)) as
			| TitleWidget
			| undefined;
	}

	private getSummaryWidget(
		hidden: Set<string>,
		slide: PreviewSlide
	): SummaryTextWidget | undefined {
		return slide.widgets.find(
			w => w.kind === 'summary_text' && !hidden.has(w.id)
		) as SummaryTextWidget | undefined;
	}
}
